import {
  CustomTableCell,
  CustomTableHead,
  FormCheckbox,
  FormDatePicker,
  FormFileInput,
  FormInput,
  FormSectionHeader,
  FormSelect,
  Loader,
} from "@/components";
import { Badge, Button, Card, CardContent, Label, RadioGroup, RadioGroupItem, Table, TableBody, TableHeader, TableRow } from "@/components/ui";
import { EyeIcon, Trash2 } from "lucide-react";
import {
  ACTUAL_CLOSING_DATE,
  ATTACHMENTS,
  CC_EMAIL,
  CLOSING_ACK,
  CLOSING_CONTACT_EMAIL,
  CLOSING_PROOF_DOCS,
  DELIVERY_METHOD,
  EMAIL_MESSAGE,
  EMAIL_SUBJECT,
  FILE_SIZE,
  FILE_TYPE,
  IS_REQUIRED,
  PROOF_FILE,
  PROOF_TYPE,
  UPLOADED_STATUS,
  uploadStatusStyle,
} from "../../consts";
import { StepsProps } from "../types";
import useStep3 from "./useStep3";
import { FormProvider } from "react-hook-form";
import { cn } from "@/lib/utils";

const Step3 = ({ onCancel, onSubmit: onBaseSubmit, isView, isAddPending }: StepsProps) => {
  const {
    form,
    requiredFields,
    fileInputRefs,
    proofDocs,
    onRemoveProof,
    onAddMore,
    isDocListLoading,
    ocpDocList,
    formData,
    onViewAttachment,
    enabledSendEmail,
    onSubmit,
    isAddDocPending,
    onSubmitClick,
    updatedKey,
    isSendEmailPending,
    showEmailSection,
    onSkip
  } = useStep3({
    onBaseSubmit,
    isView
  });
  const { errors } = form.formState;
  
  return (
    <>
      {isDocListLoading ? <Loader /> : 
        <FormProvider {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)}>
            <Card className="rounded-lg border-0 shadow-none">
              <CardContent>
                <FormSectionHeader className="text-[15px] font-semibold text-g-700 flex flex-col">
                  <span>Closing Package</span>
                  <span className="text-[12px] text-gray-400 font-normal">Proof types are populated from Master Data.</span>
                </FormSectionHeader>

                <div className="grid grid-cols-1 md:grid-cols-4">
                  <FormDatePicker
                    name={ACTUAL_CLOSING_DATE}
                    control={form.control}
                    label="Target Closing Date"
                    placeholder="Please enter"
                    error={errors?.[ACTUAL_CLOSING_DATE]?.message as string}
                    isRequired={requiredFields?.includes(ACTUAL_CLOSING_DATE)}
                    disabled
                  />
                </div>
                <div className="overflow-x-auto w-full mt-5">
                  <div className="min-w-[1000px]">
                    <Table>
                      <TableHeader>
                        <TableRow className="mb-3 rounded">
                          <CustomTableHead className="w-[150px]">
                            Proof Type
                          </CustomTableHead>
                          <CustomTableHead className="w-[150px]">
                            Proof File
                          </CustomTableHead>
                          <CustomTableHead className="w-[150px]">
                            Size
                          </CustomTableHead>
                          <CustomTableHead className="w-[150px]">
                            Status
                          </CustomTableHead>
                          <CustomTableHead className="w-[150px]">
                            Action
                          </CustomTableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {proofDocs?.length ? proofDocs?.map((_: any, index: number) => {
                          const file = formData?.[CLOSING_PROOF_DOCS]?.[index]?.[PROOF_FILE];
                          const isUploaded = formData?.[CLOSING_PROOF_DOCS]?.[index]?.[UPLOADED_STATUS] && !(formData?.[CLOSING_PROOF_DOCS]?.[index]?.[PROOF_FILE] instanceof File);
                          return (
                            <TableRow
                              key={`row-${index}-${_?.[PROOF_TYPE]}`}
                              onClick={(e) => {
                                // e.preventDefault();
                                e.stopPropagation();
                              }}
                            >
                              <CustomTableCell>
                                <div className="flex flex-row items-center gap-3">
                                  <FormSelect
                                    name={`${CLOSING_PROOF_DOCS}.${index}.${PROOF_TYPE}`}
                                    control={form.control}
                                    label=""
                                    options={ocpDocList?.map((data: any) => ({
                                      label: data?.documentName,
                                      value: data?.documentTypeId?.toString(),
                                      disabled: data?.requirementType == "REQUIRED",
                                    }))}
                                    placeholder="Please select"
                                    error={
                                      (errors as any)?.[CLOSING_PROOF_DOCS]?.[index]?.[PROOF_TYPE]?.message as string
                                    }
                                    isRequired={formData?.[CLOSING_PROOF_DOCS]?.[index]?.[IS_REQUIRED]}
                                    disabled={isView || formData[CLOSING_PROOF_DOCS][index]?.[IS_REQUIRED]}
                                  />
                                  {formData[CLOSING_PROOF_DOCS][index]?.[IS_REQUIRED] ? <Badge
                                    variant="outline"
                                    className={cn(
                                      "bg-amber-50 border-amber-600 border text-amber-600",
                                      "rounded-full px-2 py-1"
                                    )}
                                  >
                                    {"Required"}
                                  </Badge> : <Badge
                                    variant="outline"
                                    className={cn(
                                      "bg-gray-50 border-black border text-black",
                                      "rounded-full px-2 py-1"
                                    )}
                                  >
                                    {"Optional"}
                                  </Badge>}
                                </div>
                              </CustomTableCell>
                              <CustomTableCell>
                                <FormFileInput
                                  key={`${updatedKey}-${index}`}
                                  fileInputRef={fileInputRefs.current[index]}
                                  name={`${CLOSING_PROOF_DOCS}.${index}.${PROOF_FILE}`}
                                  control={form.control}
                                  label=""
                                  placeholder="Upload File"
                                  errors={
                                    (errors as any)?.[CLOSING_PROOF_DOCS]?.[index]?.[PROOF_FILE]?.message as string
                                  }
                                  isRequired={formData[CLOSING_PROOF_DOCS][index]?.[IS_REQUIRED]}
                                  disabled={isView}
                                  accept={formData[CLOSING_PROOF_DOCS][index]?.[FILE_TYPE]}
                                  maxSize={formData[CLOSING_PROOF_DOCS][index]?.[FILE_SIZE]}
                                />
                              </CustomTableCell>
                              <CustomTableCell>
                                <div className="flex items-center justify-center">{formData?.[CLOSING_PROOF_DOCS]?.[index]?.[PROOF_FILE]?.size ? `${parseFloat((formData?.[CLOSING_PROOF_DOCS]?.[index]?.[PROOF_FILE]?.size / (1024 * 1024)).toString()).toFixed(2)} MB` : "-"} </div>
                              </CustomTableCell>
                              <CustomTableCell>
                                {!!file ? <div className="flex items-center justify-center">
                                  <Badge
                                    variant="outline"
                                    className={cn(
                                      uploadStatusStyle[isUploaded ? "uploaded" : "pending"],
                                      "rounded-full px-4 py-2"
                                    )}
                                  >
                                    {isUploaded ? "Uploaded" : "Pending"}
                                  </Badge>
                                </div> : <></>}
                              </CustomTableCell>
                              <CustomTableCell>
                                <div className="flex items-center justify-center">
                                  {!!file ? (
                                    <Button
                                      variant="link"
                                      type="button"
                                      onClick={() => onViewAttachment(
                                        file?.url ? file?.url : 
                                        URL.createObjectURL(file)
                                      )}
                                    >
                                      <EyeIcon className="h-5 w-5" /> View
                                    </Button>
                                  ) : <></>}
                                  {!formData[CLOSING_PROOF_DOCS][index]?.[IS_REQUIRED] && (
                                    <div className="flex items-center">
                                      <Button
                                        type="button"
                                        variant="ghost"
                                        onClick={onRemoveProof(index)}
                                        disabled={isView}
                                      >
                                        <Trash2 className="h-6 w-6 stroke-red-600" />
                                      </Button>
                                    </div>
                                  )}
                                </div>
                              </CustomTableCell>
                            </TableRow>
                          );
                        }) : <></>}
                      </TableBody>
                    </Table>
                  </div>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={onAddMore}
                  disabled={isView}
                  className="mt-4 text-primary hover:bg-transparent hover:text-primary"
                >
                  + Add More
                </Button>
              </CardContent>
            </Card>

            {showEmailSection ? <Card className="rounded-lg border-0 shadow-none mt-5">
              <CardContent>
                <FormSectionHeader className="text-[15px] font-semibold text-g-700 flex flex-col">
                  <span>Email Closing Package</span>
                </FormSectionHeader>

                <div className="grid grid-cols-1 md:grid-cols-2 md:gap-[20px]">
                  <FormInput
                    name={`${CLOSING_CONTACT_EMAIL}`}
                    control={form.control}
                    label="Closing Contact Email"
                    placeholder="Please enter"
                    error={
                      (errors as any)?.[CLOSING_CONTACT_EMAIL]?.message as string
                    }
                    isRequired={requiredFields?.includes(CLOSING_CONTACT_EMAIL)}
                    disabled
                  />
                  <FormInput
                    name={`${CC_EMAIL}`}
                    control={form.control}
                    label="CC"
                    placeholder="Please enter"
                    error={
                      (errors as any)?.[CC_EMAIL]?.message as string
                    }
                    isRequired={requiredFields?.includes(CC_EMAIL)}
                    disabled={isView}
                  />
                  <FormInput
                    name={`${EMAIL_SUBJECT}`}
                    control={form.control}
                    label="Subject"
                    placeholder="Please enter"
                    error={
                      (errors as any)?.[EMAIL_SUBJECT]?.message as string
                    }
                    isRequired={requiredFields?.includes(EMAIL_SUBJECT)}
                    disabled={isView}
                  />
                  <FormInput
                    name={`${EMAIL_MESSAGE}`}
                    control={form.control}
                    label="Message"
                    placeholder="Please enter"
                    error={
                      (errors as any)?.[EMAIL_MESSAGE]?.message as string
                    }
                    isRequired={requiredFields?.includes(EMAIL_MESSAGE)}
                    disabled={isView}
                  />
                  <div className="md:col-span-2">
                    <RadioGroup
                      value={formData[DELIVERY_METHOD]}
                      className="ml-1 flex flex-row items-center gap-5">
                        <div className="flex flex-row items-center gap-2">
                          <RadioGroupItem
                            value={ATTACHMENTS}
                            id={ATTACHMENTS}
                            className={formData[DELIVERY_METHOD] == ATTACHMENTS ? "border-primary" : ""}
                            disabled
                          />
                          <div className="flex flex-col">
                            <Label htmlFor={ATTACHMENTS} className="font-normal">
                              {"Attach File(s)"}
                            </Label>
                            <span className="text-[10px] text-gray-500">Send all files as email attachments.</span>
                          </div>
                        </div>
                        {/* <div className="flex flex-row items-center gap-2">
                          <RadioGroupItem
                            value={DOWNLOAD_LINK}
                            id={DOWNLOAD_LINK}
                            className={formData[DELIVERY_METHOD] == DOWNLOAD_LINK ? "border-primary" : ""}
                          />
                          <div className="flex flex-col">
                            <Label htmlFor={DOWNLOAD_LINK} className="font-normal">
                              {"Downloadable Link"}
                            </Label>
                            <span className="text-[10px] text-gray-500">Include a link to download the closing documents.</span>
                          </div>
                        </div> */}
                    </RadioGroup>
                  </div>
                  <div className="md:col-span-2">
                    <FormCheckbox
                      name={`${CLOSING_ACK}`}
                      control={form.control}
                      label="I certify that I have checked all uploaded closing documents and confirm they are correct."
                      placeholder="Please enter"
                      error={
                        (errors as any)?.[CLOSING_ACK]?.message as string
                      }
                      isRequired={requiredFields?.includes(CLOSING_ACK)}
                      disabled={isView}
                    />
                  </div>
                </div>
              </CardContent>
              <div className="text-[12px] bg-amber-50 border border-amber-600 text-amber-600 p-3 rounded-md mx-4">
                Sending is enabled only after all required PDFs are uploaded and certification is checked.
              </div>
            </Card> : <></>}

            <div className="mt-7 flex justify-end gap-3">
              <Button
                type="button"
                variant="outline"
                className="min-w-[110px]"
                onClick={onCancel}
                disabled={isAddPending}
              >
                Cancel
              </Button>
              {isView ? <></> :
              <>
                {!showEmailSection ? (
                  <Button className="min-w-[90px]" disabled={isAddPending || isAddDocPending} onClick={() => onSubmitClick("save")}>
                    {isAddDocPending ? <Loader variant="secondary" /> : <></>} Save & Next
                  </Button>
                ) : (
                  <>
                    <Button
                      type="button"
                      variant="outline"
                      className="min-w-[90px]"
                      disabled={isAddPending || isSendEmailPending}
                      onClick={onSkip}
                    >
                      Skip
                    </Button>
                    <Button className="min-w-[90px]" disabled={isAddPending || isAddDocPending || isSendEmailPending || !enabledSendEmail} onClick={() => onSubmitClick("email")}>
                      {isSendEmailPending ? <Loader variant="secondary" /> : <></>} Send Email
                    </Button>
                  </>
                )}
              </>}
            </div>
          </form>
        </FormProvider>
      }
    </>
  );
};

export default Step3;